// Playback engine for a server-served replay view. Feeds the SAME
// { kind: 'full' | 'diff' } messages the live socket would into whatever sink
// GameBoard's fold funnel registers, so the board renders a replay through the
// exact code path it renders a live spectated match.
//
// Direction asymmetry is deliberate: stepping forward emits the next frame's
// diff (the board animates it the way it animates a live diff); stepping
// backward or seeking emits a Full at the target frame (the board converges
// silently, the way it does on a reconnect). Fulls are reconstructed from a
// keyframe cache built once up front, so any seek is a nearest-keyframe fold,
// never a from-scratch replay. The cached states are structurally shared
// (Immer patches), so the cache holds references, not copies.
//
// Pure logic. No DOM, no timers beyond setTimeout, injectable for tests.
import { foldInbound, makeDiff, makeFull } from '../net/matchCore.js';

const KEYFRAME_EVERY = 20;

// Playback pacing: frames that carry a feed entry get a readable beat; the
// state-plumbing frames between them fold fast so playback never crawls
// through bookkeeping. The archived journal has no timestamps, so pacing is
// synthesized rather than replayed.
const CHRON_BEAT_MS = 1300;
const PLUMBING_BEAT_MS = 180;
// Dwell AFTER a combat frame: the result scene holds on screen (instant mode,
// ~3s) before the next feed beat lands.
const COMBAT_BEAT_MS = 3600;

export class ReplayDriver {
  constructor(view, { setTimer = (fn, ms) => setTimeout(fn, ms), clearTimer = (t) => clearTimeout(t) } = {}) {
    this.view = view;
    this.frames = view.frames || [];
    this.sink = null;
    this.index = 0; // frames applied; 0 = at the dealt base
    this.playing = false;
    this.speed = 1;
    this._setTimer = setTimer;
    this._clearTimer = clearTimer;
    this._timer = null;
    this._listeners = new Set();
    this._onCombat = null;
    this._destroyed = false;

    // Keyframe cache: every KEYFRAME_EVERY-th folded state, indexed by
    // seq / KEYFRAME_EVERY (slot 0 is the base).
    this._keyframes = [view.base];
    let state = view.base;
    for (let i = 0; i < this.frames.length; i++) {
      state = this._foldOne(state, this.frames[i]);
      if (this.frames[i].seq % KEYFRAME_EVERY === 0) {
        this._keyframes[this.frames[i].seq / KEYFRAME_EVERY] = state;
      }
    }
  }

  frameCount() {
    return this.frames.length;
  }

  // GameBoard's fold funnel registers here (instead of onMatchDiff/onMatchFull).
  // The initial Full is deferred a tick so the scene finishes mounting first,
  // mirroring the network latency the live path always has.
  attach(sink) {
    this.sink = sink;
    this._setTimer(() => {
      if (this._destroyed) return;
      this._emitFullAt(this.index);
      this._emitCombatState();
    }, 0);
  }

  // Gap-heal hook for the mirror: identical contract to requestMatchFull.
  requestFull() {
    this._emitFullAt(this.index);
  }

  // Combat state is POSITION-derived, not event-derived: after every position
  // change (step, seek, playback) the callback receives the just-applied
  // frame's envelope, or null when the current frame is not a fight. The
  // scene's visibility is therefore a pure function of where the playhead
  // is: paused on a combat entry keeps the result open indefinitely.
  onCombat(fn) {
    this._onCombat = fn;
  }

  // A staged fight's frame carries its stage descriptor (one window, one
  // effect step or the completion); an older archive carries the combined
  // envelope. Either is handed to the board as-is.
  _emitCombatState() {
    const frame = this.index > 0 ? this.frames[this.index - 1] : null;
    this._onCombat?.(frame?.combatStage ?? frame?.combat ?? null);
  }

  onChange(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  getStatus() {
    return { index: this.index, playing: this.playing, speed: this.speed };
  }

  stepForward() {
    this.pause();
    this._advance();
  }

  stepBack() {
    this.pause();
    this.seek(this.index - 1);
  }

  // Jump to "state after `index` frames". Emits a Full (silent convergence).
  // A no-op when already there, so a scrub drag hovering one frame doesn't
  // re-emit full board rebuilds.
  seek(index) {
    const target = Math.max(0, Math.min(this.frames.length, index));
    if (target === this.index) return;
    this.index = target;
    this._emitFullAt(target);
    this._emitCombatState();
    this._notify();
  }

  play() {
    if (this.playing || this._destroyed) return;
    if (this.index >= this.frames.length) this.seek(0);
    this.playing = true;
    this._notify();
    this._scheduleNext();
  }

  pause() {
    if (!this.playing) return;
    this.playing = false;
    this._clearTimer(this._timer);
    this._timer = null;
    this._notify();
  }

  setSpeed(speed) {
    this.speed = speed;
    this._notify();
    if (this.playing) {
      this._clearTimer(this._timer);
      this._scheduleNext();
    }
  }

  destroy() {
    this._destroyed = true;
    this.pause();
    this.sink = null;
    this._listeners.clear();
  }

  _foldOne(state, frame) {
    return foldInbound(frame.seq - 1, state, makeDiff(frame.seq, frame.patches)).state;
  }

  _advance() {
    const frame = this.frames[this.index];
    if (!frame) {
      this.pause();
      return;
    }
    this.index += 1;
    this.sink?.(makeDiff(frame.seq, frame.patches));
    this._emitCombatState();
    this._notify();
  }

  _scheduleNext() {
    const next = this.frames[this.index];
    if (!next) {
      this.playing = false;
      this._notify();
      return;
    }
    // The dwell belongs to the frame just APPLIED when it staged a combat
    // scene; otherwise to the upcoming frame's feed weight.
    const prev = this.frames[this.index - 1];
    const sceneFrame = prev?.combatStage ? prev.combatStage.kind === 'strike' : !!prev?.combat;
    const beat = sceneFrame ? COMBAT_BEAT_MS : next.chron ? CHRON_BEAT_MS : PLUMBING_BEAT_MS;
    this._timer = this._setTimer(() => {
      if (!this.playing || this._destroyed) return;
      this._advance();
      this._scheduleNext();
    }, beat / this.speed);
  }

  _stateAt(index) {
    const key = Math.floor(index / KEYFRAME_EVERY);
    let state = this._keyframes[key];
    for (let i = key * KEYFRAME_EVERY; i < index; i++) {
      state = this._foldOne(state, this.frames[i]);
    }
    return state;
  }

  _emitFullAt(index) {
    this.sink?.(makeFull(index, this._stateAt(index)));
  }

  _notify() {
    const status = this.getStatus();
    for (const fn of this._listeners) fn(status);
  }
}
